import { Component, Input, OnChanges, output } from '@angular/core';
import {
  RadioGroupOptionV2,
  SelectItemV2,
  findItemInList,
  InputV2Module,
  RadioV2Module,
  SelectV2Module,
} from '@schenck/rotec-components';
import { DayOfWeek, DayOfWeekAppearanceInMonth, Month } from 'src/api';
import { TranslateDirective } from '@ngx-translate/core';

export interface SchedulingYearlyDayModel {
  isExact: false;
  interval: number;
  weekOfMonth: DayOfWeekAppearanceInMonth;
  dayOfWeek: DayOfWeek;
  month: number;
}

export interface SchedulingYearlyExactModel {
  isExact: true;
  dayOfMonth: number;
  interval: number;
  month: number;
}

export type SchedulingYearlyModel = SchedulingYearlyExactModel | SchedulingYearlyDayModel;

@Component({
  selector: 'ma-scheduling-yearly',
  templateUrl: './scheduling-yearly.component.html',
  styleUrls: ['./scheduling-yearly.component.less', '../scheduling-interval.less'],
  imports: [TranslateDirective, InputV2Module, RadioV2Module, SelectV2Module],
})
export class SchedulingYearlyComponent implements OnChanges {
  @Input()
  public scheduling: SchedulingYearlyModel;
  public readonly schedulingChange = output<SchedulingYearlyModel>();

  public intervalTypeItems: RadioGroupOptionV2<boolean>[] = [
    {
      value: true,
      name: 'Exactly at',
    },
    {
      value: false,
      name: 'At',
    },
  ];

  public intervalWeeksItems: SelectItemV2<DayOfWeekAppearanceInMonth>[] = [
    { value: DayOfWeekAppearanceInMonth.First, name: 'First' },
    { value: DayOfWeekAppearanceInMonth.Second, name: 'Second' },
    { value: DayOfWeekAppearanceInMonth.Third, name: 'Third' },
    { value: DayOfWeekAppearanceInMonth.Fourth, name: 'Fourth' },
    { value: DayOfWeekAppearanceInMonth.Last, name: 'Last' },
  ];

  public dayItems: SelectItemV2<DayOfWeek>[] = [
    { value: DayOfWeek.Monday, name: DayOfWeek.Monday },
    { value: DayOfWeek.Tuesday, name: DayOfWeek.Tuesday },
    { value: DayOfWeek.Wednesday, name: DayOfWeek.Wednesday },
    { value: DayOfWeek.Thursday, name: DayOfWeek.Thursday },
    { value: DayOfWeek.Friday, name: DayOfWeek.Friday },
    { value: DayOfWeek.Saturday, name: DayOfWeek.Saturday },
    { value: DayOfWeek.Sunday, name: DayOfWeek.Sunday },
  ];

  public monthItems: SelectItemV2<Month>[] = [
    { value: Month.January, name: 'January' },
    { value: Month.February, name: 'February' },
    { value: Month.March, name: 'March' },
    { value: Month.April, name: 'April' },
    { value: Month.May, name: 'May' },
    { value: Month.June, name: 'June' },
    { value: Month.July, name: 'July' },
    { value: Month.August, name: 'August' },
    { value: Month.September, name: 'September' },
    { value: Month.October, name: 'October' },
    { value: Month.November, name: 'November' },
    { value: Month.December, name: 'December' },
  ];

  public lastDayOfMonth: number = 31;

  // ========== every x years

  public interval = 1;

  public changeInterval(interval: string): void {
    this.interval = Number(interval);
    this.emitNewScheduling();
  }

  // ========== interval type

  public isIntervalExact = true;

  public intervalTypeChange(intervalType: boolean): void {
    this.isIntervalExact = intervalType;
    this.emitNewScheduling();
  }

  // ========== exactly at

  public intervalDate = 1;

  public intervalDateChange(date: string): void {
    this.intervalDate = Number(date);
    this.emitNewScheduling();
  }

  // ========== at

  public intervalWeek = this.intervalWeeksItems[0];

  public weekChange(week: SelectItemV2<DayOfWeekAppearanceInMonth>): void {
    this.intervalWeek = week;
    this.emitNewScheduling();
  }

  public dayValue = this.dayItems[0];

  public dayChange(day: SelectItemV2<DayOfWeek>): void {
    this.dayValue = day;
    this.emitNewScheduling();
  }

  // ========== month for both exactly at / at

  public monthValue = this.monthItems[0];

  public monthChange(month: SelectItemV2<Month>): void {
    this.monthValue = month;
    this.lastDayOfMonth = this.getLastDayOfMonth();
    if (this.intervalDate > this.lastDayOfMonth) {
      this.intervalDate = this.lastDayOfMonth;
    }
    this.emitNewScheduling();
  }

  public getLastDayOfMonth(): number {
    // Pick a random year that's not a leap year, so february has always 28 days.
    // Take the next month. Months start with 0 but monthValue here starts with 1. So it is already incremented.
    // ECMA-262 specifies that "day=0" is the last day of the previous month.
    return new Date(1900, this.monthValue.value, 0).getDate();
  }

  // ========== scheduling computation

  public ngOnChanges(): void {
    if (!this.scheduling) {
      return;
    }

    this.interval = this.scheduling.interval;
    this.monthValue = findItemInList(this.monthItems, this.scheduling.month);
    this.isIntervalExact = this.scheduling.isExact;

    if (this.scheduling.isExact) {
      this.setValuesForExactDayOfYearScheduling(this.scheduling);
    } else {
      this.setValuesForDayOfYearScheduling(<SchedulingYearlyDayModel>this.scheduling);
    }
  }

  private setValuesForDayOfYearScheduling(scheduling: SchedulingYearlyDayModel): void {
    this.intervalWeek = findItemInList(this.intervalWeeksItems, scheduling.weekOfMonth);
    this.dayValue = findItemInList(this.dayItems, scheduling.dayOfWeek);
  }

  private setValuesForExactDayOfYearScheduling(scheduling: SchedulingYearlyExactModel): void {
    this.intervalDate = scheduling.dayOfMonth;
  }

  private emitNewScheduling(): void {
    if (this.isIntervalExact) {
      this.emitNewExactScheduling();
    } else {
      this.emitNewDayScheduling();
    }
  }

  private emitNewExactScheduling(): void {
    this.schedulingChange.emit({
      isExact: true,
      dayOfMonth: this.intervalDate,
      interval: this.interval,
      month: <number>this.monthValue.value,
    });
  }

  private emitNewDayScheduling(): void {
    this.schedulingChange.emit({
      isExact: false,
      weekOfMonth: <DayOfWeekAppearanceInMonth>this.intervalWeek.value,
      dayOfWeek: <DayOfWeek>this.dayValue.value,
      interval: this.interval,
      month: <number>this.monthValue.value,
    });
  }
}
